import { EventEmitter } from '@breakoutlearning/firebase-repository/util'
import { createContext, useContext, useEffect } from 'react'

type UniqueElementsStore = {
  uniqueElements: Map<string, string>
  replaceElement: (key: string, id: string) => void
  events: EventEmitter
}

const store = {
  uniqueElements: new Map<string, string>(),
  replaceElement: (key: string, id: string) => {
    const current = store.uniqueElements.get(key)
    if (current !== id) {
      store.uniqueElements.set(key, id)
      store.events.emit('replaced', { id, key })
    }
  },
  events: new EventEmitter(),
}

// Use React context to make your store available in your application
const UniqueElementsContext = createContext<UniqueElementsStore>(store)
export const UniqueElementsProvider = ({
  children,
}: React.PropsWithChildren) => {
  return (
    <UniqueElementsContext.Provider value={store}>
      {children}
    </UniqueElementsContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUniqueElement(
  elementKey: string,
  elementId: string,
  onReplaced: (newId: string) => void
) {
  const store = useContext(UniqueElementsContext)
  const events = store.events

  useEffect(() => {
    const handler = ({ key, id }: { key: string; id: string }) => {
      if (key === elementKey && id !== elementId) {
        onReplaced(id)
      }
    }

    events.on('replaced', handler)

    return () => {
      events.off('replaced', handler)
    }
  }, [elementId, elementKey, onReplaced, events])

  return {
    mount: () => {
      store.replaceElement(elementKey, elementId)
    },
  }
}
